import { Linkable } from '@/components/blocks/Linkable';
import { NumberInput } from '@/components/blocks/NumberInput';
import { OrderItemTableRowData } from '@/components/content/OrderItemTable/parts/Table';
import {
	orderItemTableItemPaintColorSX,
	orderItemTableItemPaintQuantitySX,
	orderItemTableItemPaintSX,
	orderItemTableItemPaintTransparentBaseSX,
} from '@/components/content/OrderItemTable/styles/orderItemTableItemPaint';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTableRow';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { debounce } from 'lodash';
import { FC, useCallback, useContext, useMemo } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	PREMIUM_BASE_PAINTS,
	TRANSPARENT_BASE_PAINTS,
} from 'shared/custom/constants/commonConstants';

type Props = {
	rowPaints: any[];
};

export const ItemPaint: FC<Props> = ({ rowPaints }) => {
	const labels = useLocalization('HDMProductDetail');
	const {
		details,
		quantity: { quantity, onChange },
	} = useContext(ContentContext) as OrderItemTableRowData & ReturnType<typeof useOrderItemTableRow>;
	const { href } = details;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const isBase = (rowPaint: any) =>
		rowPaint?.paintValue && PREMIUM_BASE_PAINTS.includes(rowPaint.paintValue);

	const quantityChange = useCallback(
		(updatedQuantity: number | null, paintCode: string | null, paintQuantity = 0) => {
			if (updatedQuantity !== null && updatedQuantity !== paintQuantity) {
				let newQuantity = quantity;
				if (updatedQuantity > paintQuantity) {
					const diff = updatedQuantity - paintQuantity;
					newQuantity += diff;
				} else {
					const diff = paintQuantity - updatedQuantity;
					newQuantity -= diff;
				}
				onChange(newQuantity, updatedQuantity + '|' + paintCode);
			}
		},
		[onChange, quantity]
	);

	const debouncedQuantityChange = useMemo(
		() =>
			debounce(
				(updatedQuantity, paintCode, paintQuantity) =>
					quantityChange(updatedQuantity, paintCode, paintQuantity),
				500
			),
		[quantityChange]
	);

	const isTransparentBase = (paint: any) => {
		if (!paint.paintValue) return false;
		return TRANSPARENT_BASE_PAINTS.includes(paint.paintValue.toUpperCase());
	};

	return (
		<Stack sx={orderItemTableItemPaintSX.detailsContainer}>
			{rowPaints.map((paint, index) => (
				<Stack
					// TODO: add proper key based on paint
					key={index}
					direction="row"
					justifyContent="space-between"
					sx={orderItemTableItemPaintSX.detailsContainer}
				>
					{!isTransparentBase(paint) ? (
						paint.paintColorName !== '' ? (
							<>
								<Stack direction="row" sx={{ gap: '5px' }} alignItems="center">
									<Box key={index} sx={orderItemTableItemPaintColorSX(paint.paintColor)} />
									{!isMobile ? (
										isBase(paint) ? (
											<Typography variant="bodyCaption" sx={orderItemTableItemPaintSX.semibold}>
												{paint.paintColorName}
											</Typography>
										) : (
											<Stack>
												<Typography variant="bodyCaption" sx={{ color: 'grey.500' }}>
													{labels.BuyByColor.labels.code.t()}
												</Typography>
												<Typography variant="bodyCaption" sx={orderItemTableItemPaintSX.semibold}>
													{paint.paintCode}
												</Typography>
											</Stack>
										)
									) : null}
								</Stack>
								<NumberInput
									onChange={(value) =>
										debouncedQuantityChange(value, paint.paintCode, paint.paintQuantity)
									}
									value={paint.paintQuantity}
									min={0}
									sx={orderItemTableItemPaintQuantitySX(isMobile)}
									showControls
									disallowEmptyOnBlur
									data-testid="order-item-paint-quantity"
									id="order-item-paint-quantity"
									isControlled
								/>
							</>
						) : null
					) : (
						<Stack spacing={'10px'} direction="column" sx={{ width: '100%' }}>
							<Stack direction="row" sx={{ gap: '10px' }}>
								<Stack
									key={index}
									sx={orderItemTableItemPaintTransparentBaseSX(isMobile)}
									direction="row"
								>
									<DynamicIcon name={MATERIAL_SYMBOL_ICONS.brightnessAlert} />
									{!isMobile ? (
										<Typography
											variant="bodySubText"
											sx={orderItemTableItemPaintSX.transparentBase}
										>
											{labels.CartOrderItemPaintBase.t()}
										</Typography>
									) : null}
								</Stack>
								<NumberInput
									onChange={(value) =>
										debouncedQuantityChange(value, paint.paintCode, paint.paintQuantity)
									}
									value={paint.paintQuantity}
									min={0}
									sx={{ width: isMobile ? '100%' : '130px' }}
									showControls
									disallowEmptyOnBlur
									data-testid="order-item-paint-quantity"
									id="order-item-paint-quantity"
									isControlled
								/>
							</Stack>
							{isMobile ? (
								<Stack key={index} sx={orderItemTableItemPaintSX.transparentBaseContainer}>
									<Typography variant="bodySubText" sx={{ color: 'grey.500' }}>
										{labels.CartOrderItemPaintBase.t()}
									</Typography>
								</Stack>
							) : null}
							<Linkable href={{ pathname: href, hash: 'buyByColorCard' }}>
								<Stack
									direction="row"
									alignItems="flex-end"
									sx={orderItemTableItemPaintSX.transparentBaseLink}
								>
									<DynamicIcon name={MATERIAL_SYMBOL_ICONS.colors} />
									<Typography variant="bodyBaseline">
										{labels.BuyByColor.Buttons.selectColor.t()}
									</Typography>
								</Stack>
							</Linkable>
						</Stack>
					)}
				</Stack>
			))}
		</Stack>
	);
};
